import { Button, Icon, Stack } from '@chakra-ui/react';
import BigSectionTitleComponent from '@components/BigSectionTitleComponent/BigSectionTitleComponent';
import PaymentMethodForm from '@components/PaymentMethodForm/PaymentMethodForm';
import { StoreContext } from '@contexts/StoreContext';
import palette from '@theme/assets/palette';
import { SupplierNation } from '@utils/enum/SupplierNation';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MdArrowRightAlt } from 'react-icons/md';
import { BankTransfer, CreditCard, CustomerForm, TravelAccount } from 'types/formData';
import { StoreContextType } from 'types/reducers';

interface Props {
    customerForm: CustomerForm;
    nextStep: (newForm: CustomerForm) => void;
    previousStep: () => void;
    supplierNation: SupplierNation;
}

const Step2: FC<Props> = ({ customerForm, nextStep, previousStep }) => {
    const [formDataCustomer, setFormDataCustomer] = useState<CustomerForm>(customerForm);
    const {
        handleSubmit,
        register,
        control,
        formState: { errors, isSubmitting, isValid },
    } = useForm({ mode: 'onBlur' });
    const { state } = useContext<StoreContextType>(StoreContext);

    useEffect(() => {
        setFormDataCustomer(customerForm);
    }, [customerForm]);

    const onSubmit = useCallback(() => {
        if (isValid) {
            nextStep(formDataCustomer);
        }
    }, [formDataCustomer, isValid, nextStep]);

    const handleAirPaymentMethodId = useCallback(
        (id: number, object: CreditCard | BankTransfer | TravelAccount | null) => {
            if (formDataCustomer && state.paymentType) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    airPaymentMethods: {
                        ...formDataCustomer.airPaymentMethods,
                        paymentMethodId: id,
                        paymentData: object,
                    },
                });
            }
        },
        [formDataCustomer, state.paymentType],
    );

    const handleUpdateAirPaymentMethod = useCallback(
        (data: CreditCard | BankTransfer | TravelAccount | null) => {
            if (formDataCustomer && formDataCustomer.airPaymentMethods.paymentData) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    airPaymentMethods: {
                        ...formDataCustomer.airPaymentMethods,
                        paymentData: data,
                    },
                });
            }
        },
        [formDataCustomer, setFormDataCustomer],
    );

    const handleNotAirPaymentMethodId = useCallback(
        (id: number, object: CreditCard | BankTransfer | TravelAccount | null) => {
            if (formDataCustomer && state.paymentType) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    notAirPaymentMethods: {
                        ...formDataCustomer.notAirPaymentMethods,
                        paymentMethodId: id,
                        paymentData: object,
                    },
                });
            }
        },
        [formDataCustomer, state.paymentType],
    );

    const handleUpdateNotAirPaymentMethod = useCallback(
        (data: CreditCard | BankTransfer | TravelAccount | null) => {
            if (formDataCustomer && formDataCustomer.notAirPaymentMethods.paymentData) {
                setFormDataCustomer({
                    ...formDataCustomer,
                    notAirPaymentMethods: {
                        ...formDataCustomer.notAirPaymentMethods,
                        paymentData: data,
                    },
                });
            }
        },
        [formDataCustomer, setFormDataCustomer],
    );

    /* const disabledNextButton = useMemo(() => {
        const errorsKeys = Object.keys(errors);
        if (errorsKeys.length > 0) {
            return true;
        }
        if (!isValid) {
            return true;
        }
        return false;
    }, [errors, isValid]); */

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
                paddingX="20px"
                paddingTop="20px"
                paddingBottom="60px"
                justify="center"
                align="center"
                spacing="20px"
                width="900px"
                maxWidth="100%"
                margin="0 auto"
            >
                <BigSectionTitleComponent title="Metodi di pagamento Biglietteria Aerea" />
                <PaymentMethodForm
                    register={register}
                    control={control}
                    errors={errors}
                    paymentMethod={formDataCustomer.airPaymentMethods}
                    updateId={handleAirPaymentMethodId}
                    updatePaymentMethodData={handleUpdateAirPaymentMethod}
                    labelRadio="Il cliente desidera per la Biglietteria Aerea il Metodo di pagamento:"
                    id="airPayment"
                    showRiba={false}
                    isRequiredBankData={false}
                />
                <BigSectionTitleComponent title="Altra biglietteria e servizi non Air" />
                <PaymentMethodForm
                    register={register}
                    control={control}
                    errors={errors}
                    paymentMethod={formDataCustomer.notAirPaymentMethods}
                    updateId={handleNotAirPaymentMethodId}
                    updatePaymentMethodData={handleUpdateNotAirPaymentMethod}
                    labelRadio="Il cliente desidera per Altra biglietteria e servizi non Air il Metodo di pagamento:"
                    id="notAirPayment"
                    showRiba={false}
                    isRequiredBankData={false}
                />
                <Stack
                    direction="row"
                    justify="center"
                    align="center"
                    spacing="10px"
                    alignSelf="stretch"
                    padding="20px 0"
                    marginBottom="60px"
                >
                    <Button
                        size="lg"
                        variant={'outline'}
                        borderColor={palette.brand01}
                        color={palette.brand01}
                        onClick={previousStep}
                    >
                        Indietro
                    </Button>
                    <Button
                        rightIcon={<Icon as={MdArrowRightAlt} />}
                        size="lg"
                        variant={'main'}
                        bg={palette.brand01}
                        isLoading={isSubmitting}
                        type="submit"
                    >
                        Avanti
                    </Button>
                </Stack>
            </Stack>
        </form>
    );
};

export default Step2;
